import { useRef, useState } from "react";
import { KALIBRASYON_CIHAZ_ALANLARI, hedefEsit, kalibrasyonEtiketi, type GelenHedef, type KalibrasyonKartCihazi, type KalibrasyonKarti } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  KALIBRASYON_UCU,
  OKUMA_UCU,
  kalibrasyonCihazMetni,
  kalibrasyonCihazSonucu,
  kalibrasyonEklemeEtkisi,
  kalibrasyonFormu,
  kalibrasyonKarsilastirmaSatirlari,
  kalibrasyonNedenMetni,
  kalibrasyonYenidenMetni,
  okumaDugmesi,
  okumaEylemleri,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarih, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 3 · PR 16: Documents kartının kalibrasyon sertifikası "Document reading" bölümü (prototip belgeler--kalibrasyon-okuma, kart 1-4; `WpqOkumaBolumu` iskeleti,
 * çok parçalı satırlar `NdtOkumaBolumu` kalıbı). Okuma yalnız eşlemenin, kuralların, KAL-08'in ve kişi onaylı kayıt eklemenin kullandığı değerleri gösterir (sözleşme §9):
 * cihaz başına 11 değer. Marka, model, cihaz adı, müşteri, ölçüm değerleri ve imzalayan okunmaz; bitiş tarihi hesaplanmaz. Çok cihazlı dosya cihazlarına bölünür (her cihaz
 * bir sayfa aralığı); OASIS her cihazın aletini seri no ile, yoksa kodla önerir. "Use this place" kartın bugünkü tür+yer kaydını çağırır; "Use these pages" ve "Use the
 * reading" yalnız dosyalama formunu doldurur (sunucuya yazmaz). Dosyalamadan sonra öteki cihazlar "Add to Registers" ile tek tek, kişinin onayıyla YENİ kütük satırı
 * olarak eklenir; düğmeden önce Calibration end'in bugünkü ve sonraki tarihi gösterilir (D-049). `salt` kapanmış satır içindir: form ve karar düğmeleri yok; "Add to
 * Registers" sunucunun eylemiyle çizilir. Okuma excel'inin adı kartta yoktur.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";
const SATIR = "grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 px-2.5 py-2 @min-[40rem]:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1.3fr)]";
const ISO_GUN = /^\d{4}-\d{2}-\d{2}$/;
const TARIH_ALANLARI = ["kalibrasyon_tarihi", "bitis"];

const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");
/** Cihazın önerisinin anahtarı (eşleşme notundan): seri no ya da kod. */
const oneriAnahtari = (k: KalibrasyonKarti, sira: number): "seri" | "kod" => {
  const n = k.karsilastirma?.notlar.find((x) => x.tur === "eslesti" && x.cihaz === sira);
  return n?.tur === "eslesti" ? n.ile : "seri";
};

export interface KalibrasyonFormYardimi {
  /** "Use the reading": dosyalama formunun alanları (yalnız kesin değerler) */
  kullan: (alanlar: Record<string, string>) => void;
  /** "Use these pages": Pages alanı (Partial) */
  sayfalar: (s: string) => void;
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
}

export function KalibrasyonOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: KalibrasyonKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: KalibrasyonFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;
  const cok = k.cihazlar.length > 1;
  /** M1-16: cihaz seçicisi yalnız cinsi kalibrasyon ve yeri olan cihazlar; varsayılan kartın yerindeki ilk cihaz, yoksa ilk yerli cihaz. */
  const secilebilir = k.cihazlar.filter((c) => c.cins === "kalibrasyon" && c.yer.durum !== "yok" && c.yer.yer);
  const varsayilan = (secilebilir.find((c) => hedefEsit(c.yer.yer, hedef)) ?? secilebilir[0])?.sira ?? null;
  const [secim, setSecim] = useState<number | null>(null);
  const secili = secilebilir.some((c) => c.sira === secim) ? secim : varsayilan;
  /** Çok cihazlı dosyada seçilen cihazın yeri formun Record'u değilse değerleri doldurulmaz: bir cihazın sertifikası başka bir aletin formuna girmesin. */
  const seciliBaskaYerde = cok && secili !== null && !hedefEsit(secilebilir.find((c) => c.sira === secili)?.yer.yer, hedef);

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const karsilastirmaSatirlari = kalibrasyonKarsilastirmaSatirlari(k);
  const yeniden = kalibrasyonYenidenMetni(k.yenidenKarsilastirma, hedef?.tur === "kutuk" && hedef.kutuk === "alet" ? hedef.anahtar : null);
  const toplamUyari = (k.karsilastirma?.uyarilar.length ?? 0) + k.cihazlar.reduce((t, c) => t + c.uyarilar.length, 0);
  const eklenebilirVar = k.cihazlar.some((c) => c.durum === "eklenebilir");
  const aletMetni = (c: KalibrasyonKartCihazi) => (c.kayit ? cevir("Instrument {0} · {1}", c.kayit.kod, c.kayit.ad) : c.yer.yer?.tur === "kutuk" ? c.yer.yer.anahtar : null);

  const yerHucresi = (c: KalibrasyonKartCihazi) => (
    <>
      {c.yer.durum !== "yok" && aletMetni(c) && <span className="mr-1.5">{aletMetni(c)}</span>}
      {c.yer.durum !== "yok"
        ? <Etiket tone={durumTonu(c.yer.durum)}>{c.yer.durum === "kisi" ? cevir("this place") : cevir("proposed")}</Etiket>
        : <span className="text-ink-2">{kalibrasyonNedenMetni(c.yer.neden)}</span>}
      {c.yer.durum === "oneri" && <span className="block text-xs text-ink-3">{kalibrasyonNedenMetni(null, oneriAnahtari(k, c.sira))}</span>}
      {yardim && c.cins === "kalibrasyon" && c.yer.durum === "oneri" && c.yer.yer && !hedefEsit(c.yer.yer, hedef) && (
        <Buton kucuk className="mt-1" disabled={mesgul !== null} mesgul={mesgul === `yer-${c.sira}`} onClick={() => calistir(`yer-${c.sira}`, () => yardim.yerKaydet(c.yer.yer!))}>{cevir("Use this place")}</Buton>
      )}
      {yardim && cok && c.cins === "kalibrasyon" && c.yer.durum !== "yok" && (
        <Buton kucuk className="ml-1 mt-1" disabled={mesgul !== null} onClick={() => yardim.sayfalar(aralik(c.sayfalar))}>{cevir("Use these pages")}</Buton>
      )}
    </>
  );

  return (
    <div data-bolum="kalibrasyon-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && (
        <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the calibration certificate for you to check. Nothing is filed or changed. A file with several instruments is split into devices.")}</p>
      )}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              o.sayfalar ? cevir("Calibration certificate p.{0}", aralik(o.sayfalar)) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
              cok ? cevir("{0} devices", o.cihazSayisi) : null,
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          {!cok && k.cihazlar[0] && (
            <ul className="mt-2 divide-y divide-line rounded-md border border-line text-sm" data-bolum="bl-parca">
              <li className={SATIR}>
                <span>
                  <span className="block text-xs text-ink-3">{cevir("Pages")}</span>
                  <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: k.cihazlar[0].sayfalar.bas })} target="_blank" rel="noreferrer">p.{aralik(k.cihazlar[0].sayfalar)}</a>
                </span>
                <span className="min-w-0 [overflow-wrap:anywhere]"><span className="block font-medium">{kalibrasyonCihazMetni(k.cihazlar[0])}</span></span>
                <span className="col-span-2 min-w-0 [overflow-wrap:anywhere] @min-[40rem]:col-span-1">
                  <span className="block text-xs text-ink-3">{cevir("Place")}</span>
                  {yerHucresi(k.cihazlar[0])}
                </span>
              </li>
            </ul>
          )}

          {cok && (
            <>
              <AltBaslik>{cevir("Devices")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kars">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Device")}</th>
                    <th className={TABLO_BASI}>{cevir("On the certificate")}</th>
                    <th className={TABLO_BASI}>{salt ? cevir("In Registers") : cevir("Instrument")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.cihazlar.map((c) => {
                      const suphe = c.degerler.some((d) => d.kesinlik === "suphe");
                      const ekleyebilir = c.durum === "eklenebilir" && k.eylemler.cihazEkle && !c.engel;
                      const etki = c.durum === "eklenebilir" ? kalibrasyonEklemeEtkisi(c) : null;
                      return (
                        <tr key={c.sira} data-cihaz={c.sira}>
                          <td className={cn(TABLO_HUCRESI, "font-mono")}>{c.sira}<span className="block text-xs text-ink-3">p.{aralik(c.sayfalar)}</span></td>
                          <td className={TABLO_HUCRESI}><span className="inline-flex items-center gap-1.5"><Nokta k={suphe ? "suphe" : "kesin"} />{kalibrasyonCihazMetni(c)}</span></td>
                          <td className={TABLO_HUCRESI}>
                            {yerHucresi(c)}
                            {etki && <span className="block text-xs text-ink-3">{etki}</span>}
                          </td>
                          <td className={TABLO_HUCRESI}>
                            {ekleyebilir
                              ? (
                                <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === `ekle-${c.sira}`} onClick={() => calistir(`ekle-${c.sira}`, () => api.post(KALIBRASYON_UCU.cihaz(satirId), { sira: c.sira }))}>
                                  {cevir("Add to Registers")}
                                </Buton>
                              )
                              : kalibrasyonCihazSonucu(c, hedef)}
                            {c.onay && <span className="block text-xs text-ink-3">{cevir("Added by {0} on {1}", c.onay.kim, tarihSaat(c.onay.ts))}</span>}
                            {c.durum === "eklenebilir" && c.engel && <span className="block text-xs text-ink-3">{cevir(c.engel)}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-ink-3">{cevir("Each device is a page range of the file. You file one device here; the other devices that have an instrument in Registers are added after filing, one by one.")}</p>
              {eklenebilirVar && k.eylemler.cihazEkle && (
                <p className="mt-1 text-xs text-ink-3">
                  {cevir("“Add to Registers” adds one calibration certificate to the instrument with the certain values of that device (Doc no., Lab, Date, Calibration end) and its pages of this PDF. Calibration end then follows D-049, as on Registers; rows already in Registers do not change.")}
                </p>
              )}
            </>
          )}

          {k.cihazlar.map((c) => (
            <div key={c.sira} data-cihaz-ayrinti={c.sira}>
              <AltBaslik>{cevir("Device {0} · {1} · p.{2}", c.sira, c.kayit?.kod ?? c.seriNo ?? "—", aralik(c.sayfalar))}</AltBaslik>
              <ul className="space-y-0.5 text-sm">
                {KALIBRASYON_CIHAZ_ALANLARI.map((a) => {
                  const d = c.degerler.find((x) => x.alan === a.alan);
                  const deger = TARIH_ALANLARI.includes(a.alan) && d?.deger && ISO_GUN.test(d.deger) ? tarih(d.deger) 
                    // Cins listedeki adla çevrilir; Conformity değeri olduğu gibi yazılır (sözlükte "Pass" kaynak pasosudur).
                    : d?.deger && a.alan === "belge_cinsi" && d.kesinlik === "kesin" ? cevir(d.deger) : d?.deger;
                  return (
                    <li key={a.alan} className="flex gap-2">
                      <Nokta k={d?.kesinlik ?? "bos"} />
                      <span className="w-[10rem] shrink-0 text-ink-2">{cevir(kalibrasyonEtiketi(a.alan))}</span>
                      <span className="min-w-0 [overflow-wrap:anywhere]">
                        {deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                        {sayfaLinki(d?.sayfa ?? null)}
                        {d?.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {yardim && secili !== null && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              {cok && (
                <label className="flex items-center gap-1.5 text-sm">
                  <span className="text-ink-2">{cevir("Device to use")}</span>
                  <select className="rounded-md border border-line bg-surface px-2 py-1 text-sm" value={secili} disabled={mesgul !== null} onChange={(e) => setSecim(Number(e.target.value))}>
                    {secilebilir.map((c) => <option key={c.sira} value={c.sira}>{cevir("Device {0} · {1} · p.{2}", c.sira, c.kayit?.kod ?? c.seriNo ?? "—", aralik(c.sayfalar))}</option>)}
                  </select>
                </label>
              )}
              <Buton kucuk disabled={mesgul !== null || seciliBaskaYerde} onClick={() => {
                const f = kalibrasyonFormu(k, secili);
                yardim.kullan(f.alanlar);
                if (f.sayfa) yardim.sayfalar(f.sayfa);
              }}>{cevir("Use the reading")}</Buton>
              {seciliBaskaYerde && (
                <span className="w-full text-xs text-ink-2">{cevir("Device {0} is not on the instrument in Record. Use its place first (“Use this place”), then use the reading.", secili)}</span>
              )}
              <span className="w-full text-xs text-ink-3">
                {cevir("“Use the reading” fills Doc no., Lab, Date, Calibration end and Pages with the certain values of the chosen device; uncertain and empty values stay empty for you to type. Nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          <p className="mt-1 text-xs text-ink-3">
            {cevir("Not read: brand, model, instrument name, tracking number, verification code, measured values, resolution or class, reference standards, environment, method, traceability and the signer; the customer named on the certificate (its rule waits for the company list). OASIS does not compute a due date: the interval rule waits for approval.")}
          </p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {(karsilastirmaSatirlari.length > 0 || yeniden) && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
              {yeniden && (
                <li className="flex gap-2">
                  <Nokta k="kesin" />
                  <span className="min-w-0">{yeniden}</span>
                </li>
              )}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <div className="space-y-2">
                  {k.cihazlar.filter((c) => c.uyarilar.length > 0).map((c) => (
                    <div key={c.sira} data-cihaz-uyari={c.sira}>
                      <p className="text-xs font-medium text-ink-2">{cevir("Device {0}", c.sira)}</p>
                      <ul className="mt-0.5 space-y-1.5">
                        {c.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                      </ul>
                    </div>
                  ))}
                  {k.karsilastirma.uyarilar.length > 0 && (
                    <ul className="space-y-1.5">
                      {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                    </ul>
                  )}
                </div>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(KALIBRASYON_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(KALIBRASYON_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(KALIBRASYON_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
